import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getInstanceIdFromUrl } from "@/utils/instanceId";
import { getAccessToken } from "@/utils/tokenProvider";
import type { IRootReducerState } from "@/store/IRootReducer";
import { NON_RETRYABLE_STATUS_CODES } from "./retryPolicy";

const MAX_RETRIES = 2;

// Retry policy lives in its own browser-global-free module so its guard test
// can import it under the node environment (baseApi reaches MSAL at module
// scope). See retryPolicy.ts for why each status is on the list.

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/api/v1",
  // Acquired per request rather than read from a Redux snapshot. The snapshot
  // was only ever written by a 50-minute timer, which a backgrounded tab or a
  // sleeping machine silently skips while the ~60-minute token expires anyway —
  // see the header comment in `tokenProvider`. Awaiting here is close to free:
  // both this module and MSAL cache, so the steady state is a timestamp check.
  prepareHeaders: async (headers, { getState }) => {
    const token = await getAccessToken();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      return headers;
    }

    // The provider yields null only when it cannot produce a token at all —
    // MSAL still initializing on first paint, or a redirect already under way.
    // Fall back to whatever boot put in Redux so this is never WORSE than the
    // behaviour it replaces; an empty header simply becomes the server's 401.
    const state = getState() as IRootReducerState;
    const fallback = state.authReducer?.accessToken;
    if (fallback) {
      headers.set("Authorization", `Bearer ${fallback}`);
    }

    return headers;
  },
});

// Wrap rawBaseQuery to handle non-retryable errors
const baseQueryWithErrorHandling: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  // If error is non-retryable, fail immediately
  if (result.error) {
    const status = result.error.status;
    if (typeof status === "number" && NON_RETRYABLE_STATUS_CODES.includes(status)) {
      retry.fail(result.error);
    }
  }

  return result;
};

const baseQueryWithRetry = retry(baseQueryWithErrorHandling, {
  maxRetries: MAX_RETRIES,
});

// Paths that don't require an instance ID (global endpoints)
const INSTANCE_ID_EXEMPT_PATHS = ["/admin/rbac", "/auth/session", "/auth/resolve-instance"];

const isInstanceIdExempt = (url: string): boolean => {
  return INSTANCE_ID_EXEMPT_PATHS.some((path) => url.includes(path));
};

const baseQueryWithInstanceId: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : args.url;

  // Skip instance ID check for exempt paths (global endpoints)
  if (isInstanceIdExempt(url)) {
    const modifiedArgs: FetchArgs = typeof args === "string" ? { url: args } : { ...args };
    return baseQueryWithRetry(modifiedArgs, api, extraOptions);
  }

  const instanceId = getInstanceIdFromUrl();

  if (!instanceId) {
    console.warn("[API] No instance ID available - API request may fail");
    return {
      error: {
        status: 400,
        statusText: "Bad Request",
        data: {
          error: "No instance selected",
          code: "MISSING_INSTANCE_ID",
          message:
            "Please select an instance to continue. Try refreshing the page or selecting a community from the navigation.",
        },
      } as FetchBaseQueryError,
    };
  }

  let modifiedArgs: FetchArgs;

  if (typeof args === "string") {
    const urlAlreadyHasInstanceId = args.toLowerCase().includes("instanceid=");
    if (urlAlreadyHasInstanceId) {
      modifiedArgs = { url: args };
    } else {
      const separator = args.includes("?") ? "&" : "?";
      modifiedArgs = {
        url: `${args}${separator}instanceId=${instanceId}`,
      };
    }
  } else {
    const url = args.url;
    const urlAlreadyHasInstanceId = url.toLowerCase().includes("instanceid=");
    const paramsInstanceId = args.params?.instanceId;
    const paramsHasValidInstanceId =
      typeof paramsInstanceId === "string" && paramsInstanceId.length > 0;
    if (urlAlreadyHasInstanceId || paramsHasValidInstanceId) {
      modifiedArgs = { ...args };
    } else {
      const separator = url.includes("?") ? "&" : "?";
      modifiedArgs = {
        ...args,
        url: `${url}${separator}instanceId=${instanceId}`,
      };
    }
  }

  return baseQueryWithRetry(modifiedArgs, api, extraOptions);
};

/**
 * Recover from an expired token instead of surfacing it as a dead screen.
 *
 * `prepareHeaders` acquires per request, so a 401 here means the token was
 * valid when it left and is not now — the session was revoked or invalidated
 * server-side mid-flight, or the acquisition fell back to a stale token during
 * a network blip. Force a genuine refresh (bypassing both caches) and replay
 * once. The replay re-enters the whole chain, so it rebuilds its headers and
 * carries the NEW token rather than the one that just failed.
 *
 * Exactly one replay: `getAccessToken` either returns a different token or it
 * does not, and a second attempt with the same credentials cannot succeed. If
 * the refresh yields nothing, the provider has already started a login
 * redirect, and the original 401 is returned untouched so callers still see a
 * real error rather than a promise that never settles.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQueryWithInstanceId(args, api, extraOptions);

  if (result.error?.status !== 401) {
    return result;
  }

  const refreshed = await getAccessToken({ forceRefresh: true });
  if (!refreshed) {
    return result;
  }

  return baseQueryWithInstanceId(args, api, extraOptions);
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Communities",
    "ClinicalIntegrations",
    "YardiIntegrations",
    "MarketingIntegrations",
    "SkycrmCommunities",
    "SkycrmPrivacyLevels",
    "SkycrmFloorPlans",
    "SkycrmFloorPlanMarketRates",
    "SkycrmFloorPlanOccupancyPoints",
    "Scheduler",
    "Locations",
    "Operators",
    "Owners",
    "Templates",
    "CrmMessageTemplates",
    "CrmStages",
    "CrmEpisodes",
    "CrmActivityResults",
    "CrmActivityTypes",
    "CrmBarriers",
    "CrmCareTypes",
    "CrmDiscovery",
    // Discovery Coach — its OWN tag, not the CrmDiscovery one. A generated set
    // must survive a discovery edit: confirming a motivator invalidates
    // CrmDiscovery, and sharing the tag would silently re-fetch (and blank) the
    // coaching panel the counselor is reading from.
    "CrmDiscoveryCoach",
    // PLC W13 Inc-3 — the instance's Discovery Coach settings (the follow-up invitation).
    "CrmDiscoveryCoachSettings",
    // PLC W11 Inc-3 — the instance's call-recording switch (admin card + Call dialog notice).
    "CrmCallRecordingSettings",
    // vertical-lead-profile: the platform-admin lead-profile override page.
    "LeadProfileAdmin",
    "CrmMotivators",
    // D1 (V190) — the closed prospect-action vocabulary, and a person's
    // commitments. TWO tags, not one: the vocabulary is instance-wide and
    // changes when an admin edits Setup, while the commitments list is
    // person-scoped and changes every time a counselor records an ask. Sharing a
    // tag would refetch every open prospect's card on a Setup save.
    "CrmCommitmentTypes",
    "CrmCommitments",
    "CrmRelationships",
    "CrmInboxThread",
    "CrmInboxList",
    "CrmUnreadCount",
    "CrmUnassignedCount",
    "CrmUserSignatures",
    // V186 — the caller's own CRM profile (their saved click-to-call number).
    "CrmMyProfile",
    "CrmCalendar",
    "CrmCalendarPrefs",
    "CrmDashboardPrefs",
    // G9 Inc-3 — the three MHMDA consumer-health-data consent channels.
    "CrmCareDataConsent",
    "CrmDashboardRolePrefs",
    "CrmCustomReports",
    "CrmReportSchedules",
    // Advisor-surface report schedules (/advisor/report-schedules). Distinct from
    // CrmReportSchedules: the two mounts list disjoint rows (D5), so a write on one
    // surface never needs to refetch the other.
    "AdvisorReportSchedules",
    // PLC W16 Inc-3 — named distribution lists (a delete changes schedules' audiences).
    "CrmDistributionLists",
    // PLC W16 Inc-4 — report packages (crmReportPackagesApi).
    "CrmReportPackages",
    "CrmReportRun",
    // PLC W5 Inc-5 — the caller's own column layout per report (crmReportViewPrefsApi).
    "CrmReportViewPrefs",
    // G10 Inc-2 — the audit retention policy rows, and the dry-run figures that
    // are computed AGAINST them (so a policy save refreshes the report too).
    "CrmAuditRetention",
    "CrmAuditRetentionDryRun",
    // G18 — outbound delivery destinations and the per-artifact delivery log.
    // Two tags, not one: a delivery outcome changes far more often than a
    // destination's configuration, and sharing a tag would refetch the whole
    // destination list on every drain tick the operator happens to observe.
    "CrmDeliveryDestinations",
    "CrmDeliveries",
    "CrmZoomConnection",
    "Surveys",
    "SurveyRequests",
    "SurveyResponses",
    "SurveyTemplates",
    "ChatSessions",
    "ASAs",
    "AsaPricing",
    "AsaAmenities",
    "AsaMedia",
    "AsaFloorPlans",
    "AsaPhotos",
    "AsaTestimonials",
    "AsaLeadMagnets",
    "AsaFaq",
    "AsaBanners",
    "AsaTourSettings",
    "AsaAvailability",
    "AsaTargeting",
    "AsaFlow",
    // H3 — the per-ASA language list plus its coverage figures. ONE tag, not
    // two: coverage is computed FROM the same read, so there is no state in
    // which a caller wants the list refreshed and the coverage kept.
    "AsaLanguages",
    "AsaCustomCategories",
    "AsaCustomItems",
    "PhoneNumbers",
    "EmailDomains",
    "EmailAddresses",
    "Training",
    "Leads",
    "Lead",
    "LeadAssessments",
    "AssignableUsers",
    "CrmIntegrations",
    "InquiryTypes",
    "ModalConfigs",
    "AutoTriggers",
    "WidgetPlacements",
    "ModalTests",
    "AsaSmsAlertSettings",
    "AsaAlertSubscriptions",
    "AsaAlertTemplates",
    "AsaStaffProfiles",
    "ContentFolders",
    "ContentAssets",
    "LandingPages",
    "LandingPageSections",
    "LandingPageTemplates",
    "Assessments",
    "Assessment",
    "AssessmentTemplates",
    "Tours",
    "Tour",
    "AssessmentCategories",
    "AssessmentQuestions",
    "QuestionBankStats",
    "AssessmentTemplatesList",
    "AssessmentTemplateDetail",
    "TemplateQuestions",
    "ScoreLevels",
    "AssessmentsList",
    "AssessmentDetail",
    "RBAC",
    "Households",
    "Household",
    "HouseholdAttribution",
    "PushPreferences",
    "Campaigns",
    "Campaign",
    "DripCampaigns",
    "DripCampaign",
    "DripCampaignEnrollments",
    "Session",
    "NotificationPreferences",
    "DigestHistory",
    "PlatformConfig",
    "CallManagement",
    "CallbackRequest",
    "InstalledPacks",
    "TenantTerminology",
    "TenantCustomFieldDefs",
    "CrmPersons",
    "ProspectShares",
    "CrmResidents",
    "CrmResidentFiles",
    "CrmProspectFiles",
    "CrmProspectLists",
    // G15 — saved dynamic-list segment DEFINITIONS (skycrm_segments).
    "CrmSegments",
    "CrmOccupancy",
    "CrmTransitions",
    "CrmUnits",
    "CrmWaitlist",
    "CrmReferrers",
    "CrmReferrerPersons",
    "CrmOrganizations",
    "CrmReferrerPersonCommunities",
    "CrmReferrerPersonFiles",
    "CrmEvents",
    "CrmCommunityEvents",
    "CrmEventGuests",
    "CrmDayPlanner",
    // PLC W12 Inc-3a — coverage windows (out of office). A write also re-reads the
    // planner / events / activities / roster, whose "mine" views and annotations
    // follow the windows.
    "CrmCoverage",
    // Agent Alerts — its OWN tag, not CrmDayPlanner's. The two surfaces share
    // rule definitions but not a cache: dismissing an alert must not blank the
    // planner the counselor is reading beside it.
    "CrmAgentAlerts",
    "CrmSlaConfig",
    "CrmBiFeedConfig",
    "CrmScoringConfig",
    "CrmAiSettings",
    // G9 §12 F8 item 1 — the consent enforcement dial (compliance, NOT AI
    // governance; separate tag so a dial flip does not churn the AI caches).
    "CrmConsentEnforcement",
    "CrmAiPendingSends",
    "CrmAiActions",
    // V229 — the preservation orders that suspend retention. Its own tag, not
    // folded into CrmAiSettings: placing a hold must refresh the hold list and
    // nothing else, and a settings save must not churn it.
    "CrmLegalHolds",
    // skyBrain Inc-3 — the durable facts skyAgent recalls.
    "CrmBrainFacts",
    // skyAgent Skills (V191) — the written playbooks a person gives the agent.
    // Its own tag rather than sharing CrmBrainFacts: a fact and a playbook are
    // edited from different screens, and sharing one tag would refetch the whole
    // fact list every time somebody toggled a playbook.
    "CrmAgentSkills",
    // Connectors (remote MCP servers). TWO tags, not one: the server list carries
    // both the customer-level registration AND my own connection state, so
    // connecting an account must refresh it — while a per-tool permission change
    // should not re-run every row's health probe.
    "CrmMcpServers",
    "CrmMcpTools",
    // V227 — the tenant's outbound-host allowlist for MCP connectors. Its OWN
    // tag: an allowlist edit changes where connectors may reach, not which
    // connectors exist, and sharing CrmMcpServers would refetch every row on
    // each host added.
    "CrmEgressAllowlist",
    // V192. Two tags, because the two features are gated separately and surface in
    // different places — attaching a chat to a prospect should not refetch that
    // prospect's drafts, and vice versa.
    "CrmAgentPins",
    "CrmAgentDrafts",
    "CrmAgentAction",
    "CrmAutomations",
    "CrmStandupBoard",
    "CrmHealth",
    "CrmGoals",
    "CrmOverview",
    "CrmForecast",
    "CrmNbaFeed",
    "CrmNbaWalkback",
    "CrmExportJobs",
    "CrmMe",
    "CrmActivities",
    "CrmOrganizationRoles",
    "CrmCommunities",
    "CrmAnnouncements",
    "CrmUserAdmin",
    "LeadSources",
    "CrmCloseReasons",
    // PLC W7 Inc-3 — the managed competitor list (competitorsApi.ts).
    "CrmCompetitors",
    // PLC W9 Inc-2 — the managed concession type list (concessionTypesApi.ts).
    "CrmConcessionTypes",
    // PLC W8 Inc-5 — lead-source groups, a reporting overlay (leadSourceGroupsApi.ts).
    "CrmLeadSourceGroups",
    // PLC Section 1 Inc-4 — saved community groups (crmCommunityGroupsApi.ts).
    "CrmCommunityGroups",
    // PLC W12 Inc-4 — stand-up distribution lists (crmStandupDistributionsApi.ts).
    "CrmStandupDistributions",
    // PACE (pipeline_funnel) — the prospect pipeline stages from /lifecycle/stages.
    "LifecycleStages",
    // The Insights stage funnel — stage names + reached counts. Invalidated by a
    // stage rename and a lead stage move so Insights never shows a stale name/count.
    "ConversionFunnel",
    "CrmMoveOutReasons",
    // PLC W7 Inc-4: the 'choice' reasons' detail options (both catalogs).
    "CrmReasonDetailOptions",
    "ApiTokens",
    // E12 Inc2 — the subscription list and the per-subscription delivery log are
    // separate tags: a test send or a replay must refresh the log without
    // re-fetching every subscription in the instance.
    "Webhooks",
    "WebhookDeliveries",
    "AsaCommunities",
    "AsaContentCommunity",
    // skyAgent (SkyCRM) — the history rail vs one opened thread. Separate tags so
    // finishing a turn refreshes the rail without re-fetching every thread body.
    "CrmSkyAgentSessions",
    "CrmSkyAgentSession",
  ],
  endpoints: () => ({}),
});

export default baseApi;
